import { ModalStackIdleContext, TryShowModalContext } from 'contexts/ModalProvider'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { useLabRouteRequestContext } from 'hooks/useLabRouteRequestContext'
import { useActiveStatusMatchCampaigns } from 'loyalty/hooks/useActiveStatusMatchCampaign'
import useMyStatusMatchEnrollment from 'loyalty/hooks/useMyStatusMatchEnrollment'
import type { StatusMatchEnrollmentState } from 'loyalty/hooks/useMyStatusMatchEnrollment'
import { getEnrolledModalSeenId, hasSeenEnrolledModal, recordEnrolledModalSeen } from 'loyalty/storage/statusMatchEnrolledModal'
import type { EnrolledModalEnrollment } from 'loyalty/storage/statusMatchEnrolledModal'
import getCanJoinLoyalty from 'loyalty/selectors/getCanJoinLoyalty'
import getIsLoyaltyAccountClosed from 'loyalty/selectors/getIsLoyaltyAccountClosed'
import { getEnrolledModalCopy, getEnrolledModalRouteBlacklist } from 'loyalty/statusMatch/statusMatchEnrolledModal'
import { toLoyaltyTier } from 'loyalty/statusMatch/loyaltyTier'
import { isPathMatchedByPatterns } from 'loyalty/statusMatch/routePattern'
import type { StatusMatchEnrolledModalCopy } from 'loyalty/statusMatch/statusMatchTypes'
import { isChallengeCampaign } from 'loyalty/statusMatch/statusMatchTypes'
import { lazy, Suspense, useContext, useEffect, useMemo, useRef } from 'react'
import { selectLoggedIn } from 'selectors/accountSelectors'

// Lazy so the modal (and its confetti dependency) stays out of the main bundle
const StatusMatchSuccessModal = lazy(() => import('loyalty/pages/statusMatch/StatusMatchSuccessModal'))

/**
 * Rendered next to the lazy modal inside the same Suspense boundary, so its effect only runs once the modal
 * has actually committed — a failed chunk load never marks the enrollment as seen.
 *
 * Once handed to ModalProvider the modal outlives the gate, so it also closes itself when the member is logged out
 * (e.g. logging out from the account drawer frees the modal stack before the logout lands). It isn't marked seen,
 * so the gate shows it again after the next login.
 */
function EnrolledModalLoggedInGuard({ enrollmentId, expiresAt }: EnrolledModalEnrollment) {
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const modalElementContext = useModalElementContext()

  useEffect(() => {
    if (isLoggedIn) {
      recordEnrolledModalSeen({ enrollmentId, expiresAt })
    } else {
      // Close through the normal path so ModalProvider's close transition still frees the stack
      modalElementContext.resolve()
    }
  }, [isLoggedIn, enrollmentId, expiresAt, modalElementContext])

  return null
}

function getTargetTier(state: StatusMatchEnrollmentState): App.LoyaltyTier | undefined {
  return toLoyaltyTier(state.enrollment.metadata?.targetTier) ??
    (isChallengeCampaign(state.config) ? toLoyaltyTier(state.config.targetTier) : undefined)
}

interface Candidate {
  state: StatusMatchEnrollmentState
  copy: StatusMatchEnrolledModalCopy
  targetTier: App.LoyaltyTier
}

function StatusMatchEnrolledModalTrigger() {
  const tryShowModal = useContext(TryShowModalContext)
  const isModalStackIdle = useContext(ModalStackIdleContext)
  const { enrollmentStates } = useMyStatusMatchEnrollment()
  const canJoinLoyalty = useAppSelector(getCanJoinLoyalty)
  const isLoyaltyAccountLoading = useAppSelector(state => state.loyalty.account.fetching || state.loyalty.account.initial)
  const isLoyaltyAccountClosed = useAppSelector(getIsLoyaltyAccountClosed)
  // Region-stripped path, matched the same way as Lab's modal pop-up route blacklist
  const { currentPath } = useLabRouteRequestContext()
  // Seen is only recorded once the modal has mounted (see EnrolledModalLoggedInGuard), so this stops the effect
  // showing the same enrollment again in the meantime — and covers a failed storage write for the session
  const shownEnrollmentIds = useRef(new Set<string>())

  const candidates = useMemo<Array<Candidate>>(() => enrollmentStates.flatMap(state => {
    if (state.enrollment.status !== 'ACTIVE') return []
    const copy = getEnrolledModalCopy(state.config)
    const targetTier = getTargetTier(state)
    if (!copy || !targetTier) return []
    return [{ state, copy, targetTier }]
  }), [enrollmentStates])

  useEffect(() => {
    // Consent is already handled: Routes only mounts the gate once post-consent prompts can show
    // Wait for the loyalty account so non-members get the join CTA from the start
    if (!isModalStackIdle || isLoyaltyAccountLoading) return
    // A closed account can't earn the tier until it's reopened. It isn't marked seen, so it shows once they reopen.
    if (isLoyaltyAccountClosed) return

    // Seen is checked here rather than in the memo so it's re-read after each modal closes
    const candidate = candidates.find(({ state }) => {
      return !shownEnrollmentIds.current.has(getEnrolledModalSeenId(state.enrollment)) &&
        !isPathMatchedByPatterns(currentPath, getEnrolledModalRouteBlacklist(state.config)) &&
        !hasSeenEnrolledModal(state.enrollment)
    })
    if (!candidate) return

    const { state, copy, targetTier } = candidate
    tryShowModal(() => {
      shownEnrollmentIds.current.add(getEnrolledModalSeenId(state.enrollment))
      return <Suspense fallback={null}>
        <EnrolledModalLoggedInGuard enrollmentId={state.enrollment.enrollmentId} expiresAt={state.enrollment.expiresAt} />
        <StatusMatchSuccessModal
          targetTier={targetTier}
          config={state.config}
          expiresAt={state.enrollment.expiresAt}
          copy={copy}
          offerJoinSociete={canJoinLoyalty}
        />
      </Suspense>
    })
  }, [candidates, currentPath, canJoinLoyalty, isLoyaltyAccountClosed, isLoyaltyAccountLoading, isModalStackIdle, tryShowModal])

  return null
}

/**
 * Shows the "You're enrolled" modal once per enrollment when a member returns to the site.
 * Enabled per campaign via the `enrolledModal` object in its `enableStatusMatch` payload.
 */
function StatusMatchEnrolledModalGate() {
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const campaigns = useActiveStatusMatchCampaigns()
  const hasEnabledModal = campaigns.some(campaign => !!getEnrolledModalCopy(campaign.config))

  // Skip the enrollments request entirely unless a campaign has the modal enabled
  if (!isLoggedIn || !hasEnabledModal) return null

  return <StatusMatchEnrolledModalTrigger />
}

export default StatusMatchEnrolledModalGate
